import { useEffect } from 'react';

import type { AlbumView } from '@zola/svc-web-api-ts-client';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import onboardTrackingConstants from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';

import cx from 'classnames';

import { useAppDispatch, useAppSelector } from '~/store';

import { ALBUM_TYPE_BADGE, ALBUM_TYPE_DESCRIPTION } from './constants';
import { requestAlbums } from '../../../../../actions/ShopActions';
import { getShopAlbums } from '../../../../../selectors/shopSelectors';
import { convertCurrencyFormat } from '../../../../../util/currencyFormatter';
import { trackCtaClicked, trackProductClicked } from '../../../../../util/trackingHelper';

import styles from './styles.module.less';

const { BUSINESS_UNIT } = onboardTrackingConstants;

const EXPLORE_ALL_CTA = 'Explore all in Albums';
const PLP_URL = '/wedding-albums/shop';
const ALBUM_IMAGE_STYLES: { [key: string]: string } = {
  classic: styles.albumImageClassic,
  premier: styles.albumImagePremier,
  deluxe: styles.albumImageDeluxe,
};

const getPDPLink = (albumType: string): string => {
  return `/wedding-albums/select?album_type=${albumType}`;
};

type AlbumTileProps = {
  album: AlbumView;
  position: number;
  searchQuery: string;
};

const AlbumTile = ({ album, searchQuery, position }: AlbumTileProps): JSX.Element | null => {
  const { name, key, default_variation, uuid } = album;
  if (!uuid || !name || !key || !default_variation) return null;

  const price = (default_variation.price_cents || 0) / 100;
  const pdpURL = getPDPLink(key);

  const handleClick = (e?: React.MouseEvent) => {
    // Prevent the default link click (to be sure the analytics call can complete)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    trackProductClicked({
      product_id: uuid,
      sku_id: default_variation.sku_object_id,
      category: 'photo_albums',
      name,
      price,
      quantity: 1,
      position,
      url: pdpURL,
      location: 'SEARCH',
      section: 'ALBUM_SEARCH_RESULTS',
      variant: default_variation.uuid,
      search_term: searchQuery,
    });

    setTimeout(() => {
      window.location.href = pdpURL;
    }, 700);
  };

  const badge = ALBUM_TYPE_BADGE[key];
  const description = ALBUM_TYPE_DESCRIPTION[key];

  return (
    <LinkV2 className={styles.albumTile} type="button" href={pdpURL} onClick={handleClick}>
      <div className={styles.albumTitleBox}>
        <h3 className={styles.albumTitle}>{name}</h3>
        {badge && <div className={styles.albumBadge}>{badge}</div>}
      </div>
      {price > 0 && (
        <div className={styles.albumPriceBox}>
          Starts at {convertCurrencyFormat(price, 'USD', 0, 0)}
        </div>
      )}
      <div className={styles.albumDescription}>{description}</div>
      <div className={cx(styles.albumImage, ALBUM_IMAGE_STYLES[key])} />
    </LinkV2>
  );
};

interface AlbumSearchResultsProps {
  searchQuery: string;
  updateStickySidebar: () => void;
}

const AlbumSearchResults = ({
  searchQuery,
  updateStickySidebar,
}: AlbumSearchResultsProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const albumTypes: AlbumView[] = useAppSelector(getShopAlbums);

  // Fetch all album details on first render
  useEffect(() => {
    dispatch(requestAlbums()).finally(() => {
      // Force the sticky sidebar to update its position since album results may have pushed it down the page.
      updateStickySidebar();
    });
  }, [dispatch, updateStickySidebar]);

  // If there are no albums to show, render nothing.
  if (!albumTypes || albumTypes.length < 1) return null;

  const handlePLPLinkClick = (e?: React.MouseEvent) => {
    // Prevent the default link click (to be sure the analytics call can complete)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    trackCtaClicked({
      business_unit: BUSINESS_UNIT.PHOTOBOOK,
      cta_type: 'LINK',
      location: 'SEARCH',
      section: 'ALBUM_SEARCH_RESULTS',
      cta: EXPLORE_ALL_CTA,
    });

    setTimeout(() => {
      window.location.href = PLP_URL;
    }, 700);
  };

  return (
    <div className={styles.searchWrapper}>
      <div className={styles.searchContent}>
        <div className={styles.searchHeader}>
          <div className={styles.title}>
            Matches for &quot;<span className={styles.query}>{searchQuery}</span>&quot; in Albums
          </div>
          <LinkV2 className={styles.link} href={PLP_URL} arrow onClick={handlePLPLinkClick}>
            {EXPLORE_ALL_CTA}
          </LinkV2>
        </div>
        <div className={styles.searchResults}>
          {albumTypes.map((album, i) => (
            <div className={styles.result} key={album.uuid}>
              <AlbumTile album={album} searchQuery={searchQuery} position={i + 1} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlbumSearchResults;
